import { includes, isEmpty, map } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikSelect, FormikSelectProps, RequisitesInfo } from 'components';
import { useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Requisites } from 'types';

type Props = {
  name: string;
  label?: string;
  requisites?: Requisites[];
  labelClassName?: string;
  fiatCurrencyId?: string;
  crossFiatCurrencyIds?: string[];
} & Partial<FormikSelectProps>;

export const RequisitesSelect: React.FC<Props> = ({
  name,
  requisites = [],
  label,
  labelClassName,
  fiatCurrencyId,
  crossFiatCurrencyIds,
  ...rest
}: Props) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.requisites_select',
  });

  const { crossFiatPaymentTypeIds } = useUserContextUtils();

  const isDisabled = useCallback(
    (requisite: Requisites) => {
      if (crossFiatPaymentTypeIds.includes(requisite.paymentTypeId)) {
        return (
          !!fiatCurrencyId &&
          requisite.fiatCurrencyId !== fiatCurrencyId &&
          !isEmpty(crossFiatCurrencyIds) &&
          !includes(crossFiatCurrencyIds, requisite.fiatCurrencyId)
        );
      }
      return !!fiatCurrencyId && requisite.fiatCurrencyId !== fiatCurrencyId;
    },
    [crossFiatCurrencyIds, fiatCurrencyId, crossFiatPaymentTypeIds],
  );

  const options = useMemo(
    () =>
      map(requisites, (requisite) => ({
        value: requisite.id,
        label: (
          <RequisitesInfo
            compact
            className={labelClassName}
            canCopy={false}
            fiatCurrencyId={requisite.fiatCurrencyId}
            bankId={requisite.bankId}
            cardInfo={requisite?.cardInfo}
            phone={requisite?.phone}
            paymentTypeId={requisite.paymentTypeId}
            cardholder={requisite.cardholder}
            swiftBic={requisite.swiftBic}
            taxId={requisite.taxId}
            bic={requisite.bic}
            email={requisite.email}
            idCard={requisite.idCard}
            beneficiaryName={requisite.beneficiaryName}
            accountNumber={requisite.accountNumber}
            name={requisite.name}
            expirationDate={requisite.expirationDate}
            paymentLink={requisite.paymentLink}
          />
        ),
        disabled: isDisabled(requisite),
      })),
    [requisites, labelClassName, isDisabled],
  );

  return (
    <FormikSelect
      noneOption
      label={label || t('label')}
      {...rest}
      name={name}
      options={options}
    />
  );
};
